import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Attachments, FilePicker } from "@/components/Attachments";
import { ReportButton } from "@/components/ReportButton";

const time = (ts) => new Date(ts).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });

const Bubble = ({ m, mine }) => (
  <li data-testid={`chat-message-${m.msg_id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[82%] sm:max-w-[70%] rounded-2xl px-3.5 py-2.5 ${mine ? "rounded-br-md bg-navy-200 border border-gold-400/50" : "rounded-bl-md bg-surface-2 border border-slate-200"}`}>
      {!mine && <p className="text-xs font-bold text-gold-300">{m.name}</p>}
      {m.text && <p className="text-sm text-white whitespace-pre-wrap break-words">{m.text}</p>}
      <Attachments files={m.files} compact />
      <p className="mt-1 flex items-center justify-end gap-3 text-[11px] text-slate-500">{!mine && <ReportButton kind={m.thread_id ? "dm" : "message"} itemId={m.msg_id} scopeId={m.thread_id || m.group_id} />}{time(m.ts)}</p>
    </div>
  </li>
);

export const ChatBox = ({ path, me, extract = (d) => d, placeholder = "Message the group", id = "group-chat" }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const last = useRef("");
  const end = useRef(null);
  const fetchNew = useCallback(async () => {
    try {
      const { data } = await api.get(path, { params: last.current ? { after: last.current } : {} });
      const list = Array.isArray(extract(data)) ? extract(data) : [];
      if (list.length) {
        last.current = list[list.length - 1].ts;
        setMsgs((m) => { const seen = new Set(m.map((x) => x.msg_id)); return [...m, ...list.filter((x) => !seen.has(x.msg_id))]; });
      }
    } catch { /* keep polling */ }
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    last.current = ""; setMsgs([]); fetchNew();
    const t = setInterval(fetchNew, 4000);
    return () => clearInterval(t);
  }, [fetchNew]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    try { await api.post(path, fd); setText(""); setFiles([]); await fetchNew(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <section data-testid={id} className="card flex flex-col h-[calc(100dvh-20rem)] min-h-[420px] overflow-hidden">
      <ul data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-2.5">
        {msgs.length === 0 && <li className="h-full grid place-items-center text-center text-sm text-slate-500">No messages yet. Say hello.</li>}
        {msgs.map((m) => <Bubble key={m.msg_id} m={m} mine={m.user_id === me} />)}
        <li ref={end} />
      </ul>
      <form onSubmit={send} className="border-t border-slate-200 bg-surface-deep/50 p-3 space-y-2">
        {files.length > 0 && <FilePicker files={files} setFiles={setFiles} max={4} id="chat-file" />}
        <div className="flex items-end gap-2">
          {!files.length && <FilePicker files={files} setFiles={setFiles} max={4} id="chat-file" />}
          <textarea data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} rows={1} maxLength={2000} placeholder={placeholder}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
            className="flex-1 min-w-0 max-h-32 resize-none rounded-2xl border border-slate-200 bg-surface px-3.5 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
          <button data-testid="chat-send" disabled={busy || (!text.trim() && !files.length)} aria-label="Send" className="grid place-items-center w-11 h-11 shrink-0 rounded-2xl bg-gold-400 text-black hover:bg-gold-300 disabled:opacity-40 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </section>
  );
};

const pickDm = (d) => d?.messages;

export const GroupChat = ({ gid, me }) => <ChatBox path={`/groups/${gid}/messages`} me={me} />;

export const DmChat = ({ tid, me }) => <ChatBox path={`/messages/${tid}`} me={me} extract={pickDm} placeholder="Write a message" id="dm-chat" />;
